import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { CreditCard, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ApiError, api } from "../../lib/api";
import { cn } from "../../lib/utils";
import { useAuth } from "../../stores/auth";
import { SectionTitle } from "./shared";

type BillingSubscription = {
  id: string;
  status: string;
  tier: string;
  priceBookVersion: number;
  currency: string;
  paddleSubscriptionId: string | null;
  collectionMode: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  nextBilledAt: string | null;
  traderSeatsIncluded: number | null;
  traderSeatsExtra: number;
  gwhIncluded: string | null;
  graceUntil: string | null;
  scheduledCancelAt: string | null;
  scheduledPauseAt: string | null;
  updatedAt: string | null;
};

type CompanyOnboardingSummary = {
  status: "required" | "invoiced" | "paid" | "canceled" | "reversed" | "exempt";
  invoiceNumber: string | null;
  amountMinor: number;
  currency: string;
  billedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  canceledAt: string | null;
  reversedAt: string | null;
};

type CompanyOnboarding = CompanyOnboardingSummary & {
  paddlePriceId: string | null;
  paddleTransactionId: string | null;
  paddleSubscriptionId: string | null;
};

type BillingResponse = {
  subscription: BillingSubscription | null;
  // Additive during the onboarding-fee rollout. Once the backend migration is
  // live this is always present (null means no valid invoice has been issued).
  onboarding?: CompanyOnboarding | null;
  entitlements: { featureKey: string; source: string; validFrom: string }[];
  seatUsage: { used: number; allowed: number | null };
  priceBookVersion: number | null;
  finalTrueUpGuard: {
    state: "not_applicable" | "not_due" | "missing" | "unbilled" | "billed" | "waived";
    blocking: boolean;
    warningLeadDays: number;
    scheduledCancelAt: string | null;
    snapshot: {
      periodEnd: string;
      status: string;
      overageAmountMinor: number;
      paddleTransactionId: string | null;
    } | null;
  };
};
type FinalTrueUpGuard = BillingResponse["finalTrueUpGuard"];

function finalTrueUpMessage(guard: FinalTrueUpGuard): string {
  const effective = guard.scheduledCancelAt?.slice(0, 10) ?? "the cancellation date";
  if (guard.state === "billed") {
    return `The snapshot ending ${effective} is bound to ${guard.snapshot?.paddleTransactionId}.`;
  }
  if (guard.state === "waived") {
    return `The snapshot ending ${effective} has an explicit audited waiver; no charge will be issued.`;
  }
  if (guard.state === "unbilled") {
    return `The matching snapshot is ${guard.snapshot?.status ?? "not billed"}. Do not let cancellation take effect until it is billed or explicitly waived.`;
  }
  if (guard.state === "missing") {
    return `No exact final-true-up snapshot exists for ${effective}. This is inside the ${guard.warningLeadDays}-day safety window; do not let cancellation take effect.`;
  }
  return `Prepare the exact final-period snapshot before the ${guard.warningLeadDays}-day safety window. Until Phase 4 charge issuance ships, use the controlled billing runbook and preserve the resulting transaction ID.`;
}

type OnboardingDisplayState = "not_invoiced" | "invoice_due" | "paid" | "overdue" | "exempt";
const ONBOARDING_FEE_MINOR = 1_489_000;
const ONBOARDING_FEE_CURRENCY = "GBP";

function formatBillingMoney(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amountMinor / 100);
}

function formatBillingCutoff(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

function onboardingDisplayState(
  onboarding: CompanyOnboardingSummary | null,
  now = new Date()
): OnboardingDisplayState {
  if (!onboarding || ["required", "canceled", "reversed"].includes(onboarding.status)) {
    return "not_invoiced";
  }
  if (onboarding.status === "paid") return "paid";
  if (onboarding.status === "exempt") return "exempt";
  if (onboarding.status === "invoiced" && onboarding.dueAt && now >= new Date(onboarding.dueAt)) {
    return "overdue";
  }
  return onboarding.status === "invoiced" ? "invoice_due" : "not_invoiced";
}

const ONBOARDING_STATE_LABELS: Record<OnboardingDisplayState, string> = {
  not_invoiced: "Not invoiced",
  invoice_due: "Invoice due",
  paid: "Paid",
  overdue: "Overdue",
  exempt: "Exempt",
};

function onboardingStateClass(state: OnboardingDisplayState): string {
  if (state === "paid" || state === "exempt") return "border-posGreen/40 text-posGreen";
  if (state === "invoice_due") return "border-amber/40 text-amber";
  if (state === "overdue") return "border-negRed/40 text-negRed";
  return "border-border text-muted";
}

const BILLING_STATUS_OPTIONS = [
  "pending",
  "trialing",
  "active",
  "past_due",
  "suspended",
  "canceled",
  "comped",
];
const BILLING_TIER_OPTIONS = ["bronze", "silver", "gold", "platinum"];
const EEX_FEATURES: { key: string; label: string }[] = [
  { key: "gas_closeout", label: "Gas — close-out" },
  { key: "gas_delivery", label: "Gas — delivery" },
  { key: "eua", label: "EUA/EUAA" },
  { key: "isda_otc", label: "ISDA/OTC" },
  { key: "physical", label: "Physical" },
  { key: "capacities_physical", label: "Capacities — physical" },
  { key: "capacities_financial", label: "Capacities — financial" },
  { key: "exotics", label: "Exotics" },
];

function billingStatusClass(s: string): string {
  if (s === "active" || s === "comped" || s === "trialing") return "border-posGreen/40 text-posGreen";
  if (s === "past_due") return "border-amber/40 text-amber";
  if (s === "suspended" || s === "canceled") return "border-negRed/40 text-negRed";
  return "border-border text-muted"; // pending
}

// Plain-English labels for the subscription-status codes (the DB enum values stay
// as-is; this is display only). "Complimentary" is what "comped" means.
const BILLING_STATUS_LABELS: Record<string, string> = {
  pending: "Awaiting payment",
  trialing: "Trial",
  active: "Active",
  past_due: "Past due",
  suspended: "Suspended",
  canceled: "Cancelled",
  comped: "Complimentary",
};
function billingStatusLabel(s: string): string {
  return BILLING_STATUS_LABELS[s] ?? s;
}

function billingError(e: unknown): string {
  if (e instanceof ApiError) {
    const b = e.body as { error?: string; detail?: string; used?: number; allowed?: number } | undefined;
    if (b?.error === "seat_cap_below_usage") {
      return `Can't lower the cap below current usage — ${b.used} trader seats in use, new cap ${b.allowed}. Free seats first.`;
    }
    if (b?.error === "paddle_managed") return "This subscription is managed by Paddle — change it there.";
    if (b?.error === "change_locked") {
      const reasons = (e.body as { reasons?: string[] }).reasons ?? [];
      return `Change is locked: ${reasons.join("; ")}.`;
    }
    if (b?.error === "no_price_mapped") {
      const missing = (e.body as { missing?: string[] }).missing ?? [];
      return `No Paddle price mapped for: ${missing.join(", ")} — run the catalog sync first.`;
    }
    if (
      b?.error === "paddle_customer_not_found" ||
      b?.error === "paddle_customer_ambiguous" ||
      b?.error === "paddle_customer_not_invoice_ready" ||
      b?.error === "paddle_customer_address_ambiguous" ||
      b?.error === "paddle_customer_business_ambiguous" ||
      b?.error === "paddle_provisioning_uncertain" ||
      b?.error === "paddle_invoice_rejected" ||
      b?.error === "paddle_provisioning_claim_failed" ||
      b?.error === "paddle_provisioning_claim_lost" ||
      b?.error === "provisioning_in_progress" ||
      b?.error === "onboarding_invoice_already_issued" ||
      b?.error === "onboarding_reissue_required" ||
      b?.error === "onboarding_state_missing" ||
      b?.error === "onboarding_state_conflict" ||
      b?.error === "onboarding_currency_not_supported" ||
      b?.error === "onboarding_price_not_mapped" ||
      b?.error === "ambiguous_onboarding_price_mapping" ||
      b?.error === "onboarding_price_unavailable" ||
      b?.error === "onboarding_price_invalid" ||
      b?.error === "onboarding_product_invalid" ||
      b?.error === "paddle_initial_invoice_invalid"
    ) {
      return b.detail ?? "The invoice could not be issued safely. Check the onboarding state and Paddle catalog.";
    }
    if (b?.error === "not_paddle_managed") return "This company isn't on Paddle billing — edit the plan directly above.";
    if (b?.error === "no_scheduled_change") return "There's no scheduled cancellation to remove.";
    if (b?.error === "plan_drifted")
      return "The subscription changed while the form was open (another admin, the portal, or a webhook). The form was closed — reopen it to see the current plan.";
    if (b?.error === "unmirrored_items")
      return "The live subscription carries items our catalog doesn't know — changing it here would cancel them. Resolve in the Paddle dashboard or sync the catalog first.";
    if (b?.error === "component_not_available") {
      const components = (e.body as { components?: string[] }).components ?? [];
      return `Not offered on that tier: ${components.join(", ")}.`;
    }
    if (b?.error === "paddle_rejected") {
      const d = e.body as { code?: string; detail?: string };
      return `Paddle rejected the request: ${d.detail ?? d.code ?? "unknown reason"}.`;
    }
    if (b?.error === "proration_mode_not_allowed")
      return "That billing mode isn't available for invoice-collected subscriptions — pick a next-invoice option.";
    if (b?.error === "scheduled_change_mismatch") {
      const d = e.body as { actual?: string };
      return `The pending change is a ${d.actual ?? "different action"}, not a cancellation — refresh and check again.`;
    }
    if (b?.error === "month_not_closed") return "That month isn't closed yet — only past CET months can be metered.";
    if (b?.error === "unplaced_trades")
      return "Not metered: some trades in that month can't be placed (product catalog gap) — needs a look.";
    if (b?.error === "undated_trades")
      return "Not metered: this company has trades with no trade date — fix the data first.";
    if (b?.error === "no_price_book_base")
      return "Not metered: this subscription has no matching price-book base row. Seed the rate card before calculating overage.";
    if (b?.detail) return b.detail;
  }
  return e instanceof Error ? e.message : String(e);
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="ops-panel ops-admin-mini-stat border border-border/60 rounded px-2 py-1">
      <div className="text-[10px] text-muted">{label}</div>
      <div className="text-sm font-mono truncate">{value}</div>
    </div>
  );
}

function OnboardingFeeCard({
  onboarding,
  showTechnicalDetails = false,
}: {
  onboarding: CompanyOnboardingSummary | CompanyOnboarding | null | undefined;
  showTechnicalDetails?: boolean;
}) {
  const state = onboarding === undefined ? null : onboardingDisplayState(onboarding);
  const invalidated = onboarding?.status === "canceled" || onboarding?.status === "reversed";
  const detail =
    state === null
      ? "Checking the company-level onboarding purchase record."
      : state === "paid"
        ? `Payment confirmed${onboarding?.paidAt ? ` on ${formatBillingCutoff(onboarding.paidAt)}` : ""}. The onboarding requirement is satisfied.`
        : state === "exempt"
          ? "An explicit onboarding exemption is recorded for this company."
          : state === "invoice_due"
            ? onboarding?.dueAt
              ? `EEX access remains available until the payment cutoff at ${formatBillingCutoff(onboarding.dueAt)}.`
              : "The invoice is issued on Net 14 terms. The exact cutoff is awaiting Paddle synchronization."
            : state === "overdue"
              ? "The Net-14 payment cutoff has passed. EEX access remains blocked until Paddle confirms payment."
              : invalidated
                ? "The previous onboarding invoice or payment is no longer valid. A valid onboarding invoice is required for EEX access."
                : "A valid onboarding invoice has not been issued. EEX access is unavailable.";

  return (
    <div className="ops-panel border border-border/60 rounded p-3 space-y-2 max-w-md">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-medium text-fg">Mandatory onboarding</div>
        {state ? (
          <span className={cn("rounded border px-2 py-0.5 text-xs", onboardingStateClass(state))}>
            {ONBOARDING_STATE_LABELS[state]}
          </span>
        ) : (
          <span className="rounded border border-border px-2 py-0.5 text-xs text-muted">
            Syncing status
          </span>
        )}
      </div>
      <div className="flex items-start justify-between gap-4 border-y border-border/60 py-2 text-xs">
        <div>
          <div className="font-medium text-fg">Onboarding &amp; historical integration</div>
          <div className="text-[10px] text-muted">One-time · mandatory · excluding tax</div>
        </div>
        <div className="shrink-0 font-mono font-medium text-fg">
          {formatBillingMoney(ONBOARDING_FEE_MINOR, ONBOARDING_FEE_CURRENCY)}
        </div>
      </div>
      <div className="text-[11px] text-muted">
        Payment terms <span className="font-medium text-fg">Net 14</span>
        {onboarding?.dueAt ? (
          <> · Payment cutoff <span className="font-medium text-fg">{formatBillingCutoff(onboarding.dueAt)}</span></>
        ) : null}
      </div>
      <p className="text-[11px] text-muted">{detail}</p>
      {showTechnicalDetails && onboarding ? (
        <div className="text-[10px] text-muted space-y-0.5 border-t border-border/60 pt-2">
          <div>
            Invoice <span className="font-mono text-fg">{onboarding.invoiceNumber ?? "pending"}</span>
          </div>
          {"paddleTransactionId" in onboarding && onboarding.paddleTransactionId ? (
            <div>
              Transaction <span className="font-mono text-fg">{onboarding.paddleTransactionId}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ── Global billing health (P3-WP5): reconciliation + alert surface ───────────

type BillingHealth = {
  generatedAt: string;
  paddleConfigured: boolean;
  events: { parked: number; unprocessedOverAnHour: number; lastEventAt: string | null };
  webhookSilence: { silentDays: number | null; paddleBackedSubs: number; alert: boolean };
  scheduledCancels: { companyId: string; paddleSubscriptionId: string | null; effectiveAt: string }[];
  suspensionProposals: { companyId: string; graceUntil: string }[];
  drift: { companyId: string | null; paddleSubscriptionId: string; kind: string; ours: string; paddle: string; heal: boolean }[];
  driftError: string | null;
};

function HealthTile({ label, value, alert }: { label: string; value: string; alert: boolean }) {
  return (
    <div className={cn("ops-admin-health-stat border rounded px-2 py-1.5", alert ? "border-negRed/50" : "border-border/60")}>
      <div className="text-[10px] text-muted">{label}</div>
      <div className={cn("text-lg font-mono leading-tight", alert ? "text-negRed" : "text-fg")}>{value}</div>
    </div>
  );
}

export function BillingHealthPanel() {
  const q = useQuery({
    queryKey: ["admin", "billing", "health"],
    queryFn: () => api<BillingHealth>("/admin/billing/health"),
    retry: false,
    // Each fetch runs a live Paddle reconcile (an API call per subscription), so poll
    // gently — the daily cron is the real safety net; this panel is a spot-check.
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  const h = q.data;
  if (q.isLoading) return <div className="text-sm text-muted">Checking billing health…</div>;
  if (q.isError || !h) {
    // Never silently hide the alert surface — a failed health check is itself a signal.
    return (
      <div className="text-sm text-negRed">
        Couldn't load billing health{q.error instanceof Error ? `: ${q.error.message}` : ""}.
      </div>
    );
  }
  const clean =
    h.events.parked === 0 &&
    h.drift.length === 0 &&
    h.suspensionProposals.length === 0 &&
    h.scheduledCancels.length === 0 &&
    !h.webhookSilence.alert &&
    !h.driftError;

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <SectionTitle icon={<CreditCard size={18} />} title="Billing health" />
        {!h.paddleConfigured ? (
          <span className="text-[11px] text-muted">(Paddle dormant — not configured)</span>
        ) : null}
        {clean ? (
          <span className="rounded border border-posGreen/40 text-posGreen px-2 py-0.5 text-xs">All clear</span>
        ) : null}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 max-w-3xl">
        <HealthTile label="Parked events" value={String(h.events.parked)} alert={h.events.parked > 0} />
        <HealthTile
          label="Stale >1h"
          value={String(h.events.unprocessedOverAnHour)}
          alert={h.events.unprocessedOverAnHour > 0}
        />
        <HealthTile
          label="Webhook silence"
          value={h.webhookSilence.silentDays == null ? "—" : `${h.webhookSilence.silentDays}d`}
          alert={h.webhookSilence.alert}
        />
        <HealthTile
          label="Drift"
          value={h.driftError ? "err" : String(h.drift.length)}
          alert={h.drift.length > 0 || Boolean(h.driftError)}
        />
        <HealthTile
          label="Suspend proposed"
          value={String(h.suspensionProposals.length)}
          alert={h.suspensionProposals.length > 0}
        />
      </div>

      {h.driftError ? (
        <div className="text-xs text-negRed">Reconcile call failed: {h.driftError}</div>
      ) : null}

      {h.drift.length > 0 ? (
        <div className="text-xs space-y-1 max-w-3xl">
          <div className="text-muted">Drift (Paddle vs our records):</div>
          {h.drift.map((d, i) => (
            <div key={i} className="font-mono text-[11px] flex flex-wrap gap-2">
              <span className={cn("rounded border px-1.5", d.heal ? "border-amber/40 text-amber" : "border-negRed/50 text-negRed")}>
                {d.kind}
              </span>
              <span className="text-muted">{d.paddleSubscriptionId}</span>
              <span>ours=<span className="text-fg">{d.ours}</span></span>
              <span>paddle=<span className="text-fg">{d.paddle}</span></span>
              {d.heal ? <span className="text-muted">(auto-healable)</span> : <span className="text-negRed">(needs a human)</span>}
            </div>
          ))}
        </div>
      ) : null}

      {h.scheduledCancels.length > 0 ? (
        <div className="text-[11px] text-muted max-w-3xl">
          Portal-scheduled cancels:{" "}
          {h.scheduledCancels.map((c) => `${c.companyId.slice(0, 8)}@${c.effectiveAt.slice(0, 10)}`).join(", ")}
        </div>
      ) : null}
    </section>
  );
}

export function BillingSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // Declared before the query: while a just-applied Paddle change waits for its
  // webhook, the query POLLS so the mirror update actually reaches this page
  // (there's no focus-refetch and apply deliberately doesn't write our DB).
  const [changeApplied, setChangeApplied] = useState(false);
  const q = useQuery({
    queryKey: ["admin", "billing", companyId],
    queryFn: () => api<BillingResponse>(`/admin/billing/companies/${companyId}`),
    retry: false,
    refetchInterval: changeApplied ? 10_000 : false,
  });
  const sub = q.data?.subscription ?? null;
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "billing", companyId] });
    void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    // Health tiles (parked events, drift, suspension proposals) reflect these actions.
    void queryClient.invalidateQueries({ queryKey: ["admin", "billing", "health"] });
  };

  const [form, setForm] = useState<{
    status: string;
    tier: string;
    traderSeatsExtra: number;
    currency: string;
    graceUntil: string;
  } | null>(null);
  useEffect(() => {
    if (!sub) {
      setForm(null);
      return;
    }
    setForm({
      status: sub.status,
      tier: sub.tier,
      traderSeatsExtra: sub.traderSeatsExtra,
      currency: sub.currency,
      graceUntil: sub.graceUntil ? sub.graceUntil.slice(0, 10) : "",
    });
    // Re-seed when the subscription identity or server-updated timestamp changes.
  }, [sub?.id, sub?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveM = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api(`/admin/billing/companies/${companyId}/subscription`, {
        method: "PUT",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(billingError(e)),
  });
  const featureM = useMutation({
    mutationFn: (v: { key: string; active: boolean; source: string }) =>
      api(`/admin/billing/companies/${companyId}/features/${v.key}`, {
        method: "PUT",
        body: JSON.stringify({ active: v.active, source: v.source }),
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(billingError(e)),
  });

  // Access suspension is propose-and-confirm (owner policy). These work on Paddle-
  // managed subs too — suspension is our access decision, not a Paddle change.
  const suspendM = useMutation({
    mutationFn: () => api(`/admin/billing/companies/${companyId}/suspend`, { method: "POST" }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(billingError(e)),
  });
  const resumeM = useMutation({
    mutationFn: () => api(`/admin/billing/companies/${companyId}/resume`, { method: "POST" }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(billingError(e)),
  });

  const openOnboardingM = useMutation({
    mutationFn: () => api<{ operation: { id: string } }>("/admin/onboarding", {
      method: "POST",
      body: JSON.stringify({ companyId }),
    }),
    onSuccess: (result) => {
      void navigate({
        to: "/admin/onboarding/$operationId",
        params: { operationId: result.operation.id },
      });
    },
    onError: (e) => setError(billingError(e)),
  });

  // Reset company-scoped interactive state when switching clients.
  useEffect(() => {
    setChangeForm(null);
    setChangePreview(null);
    setChangeApplied(false);
    setError(null); // else company A's error message stays rendered under company B
  }, [companyId]);

  const suspensionProposed =
    sub?.status === "past_due" && sub.graceUntil != null && new Date(sub.graceUntil) <= new Date();
  const onboardingStatus = q.data?.onboarding?.status ?? null;

  // WP7: mid-term change for Paddle-managed subs. The form is seeded from the
  // SERVER's view of the LIVE Paddle items (GET …/change-subscription/current) —
  // never from our mirror or entitlement rows — and every request carries that
  // seed back as expectedCurrent, so the server can 409 (plan_drifted) if the
  // subscription changed in between. An omitted item is a cancelled item, so the
  // complete list must always describe reality the admin actually saw.
  type PlanShape = { tier: string; addons: string[]; currencies: string[]; extraSeats: number };
  type ChangeFormState = {
    desired: PlanShape;
    expectedCurrent: PlanShape;
    prorationMode: string;
    // bumped on every edit; a preview is only valid for the version it was
    // requested at (a slow response must not re-arm Apply for a changed form).
    version: number;
  };
  const [changeForm, setChangeForm] = useState<ChangeFormState | null>(null);
  const [changePreview, setChangePreview] = useState<{
    version: number;
    charge: { amount: string; currency: string } | null;
    credit: { amount: string; currency: string } | null;
    nextBilledAt: string | null;
    includedByTier: string[];
  } | null>(null);
  // Monotonic across form instances (and never reset on company switch), so a
  // preview left in flight when one form closes can never version-match a fresh
  // form — versions from different instances are always disjoint.
  const changeSeqRef = useRef(0);
  const editChange = (patch: Partial<Omit<ChangeFormState, "version" | "expectedCurrent">>) => {
    setChangePreview(null);
    setChangeForm((f) => (f ? { ...f, ...patch, version: ++changeSeqRef.current } : f));
  };
  const openChangeM = useMutation({
    mutationFn: (forCompanyId: string) =>
      api<{ current: PlanShape; collectionMode: string; locks: string[] }>(
        `/admin/billing/companies/${forCompanyId}/change-subscription/current`
      ),
    onSuccess: (r, forCompanyId) => {
      // A response for a previously-viewed company must not populate this page.
      if (forCompanyId !== companyId) return;
      setError(null);
      if (r.locks.length > 0) {
        setError(`Change is locked: ${r.locks.join("; ")}.`);
        return;
      }
      setChangeApplied(false);
      setChangePreview(null);
      setChangeForm({
        desired: { ...r.current, addons: [...r.current.addons], currencies: [...r.current.currencies] },
        expectedCurrent: r.current,
        prorationMode: "prorated_next_billing_period",
        version: ++changeSeqRef.current,
      });
    },
    onError: (e) => setError(billingError(e)),
  });
  const changeM = useMutation({
    mutationFn: (v: { apply: boolean; form: ChangeFormState; forCompanyId: string }) =>
      api<{
        preview?: boolean;
        applied?: boolean;
        charge: { amount: string; currency: string } | null;
        credit: { amount: string; currency: string } | null;
        nextBilledAt: string | null;
        includedByTier?: string[];
      }>(`/admin/billing/companies/${v.forCompanyId}/change-subscription`, {
        method: "POST",
        body: JSON.stringify({
          desired: v.form.desired,
          expectedCurrent: v.form.expectedCurrent,
          prorationMode: v.form.prorationMode,
          apply: v.apply,
        }),
      }),
    onSuccess: (r, v) => {
      if (v.forCompanyId !== companyId) return; // stale cross-company response
      setError(null);
      if (v.apply) {
        setChangeForm(null);
        setChangePreview(null);
        setChangeApplied(true);
        invalidate();
      } else {
        // Bind the preview to the form version it was requested at; a stale
        // response for an already-edited (or reopened) form must not re-arm Apply.
        setChangePreview({
          version: v.form.version,
          charge: r.charge,
          credit: r.credit,
          nextBilledAt: r.nextBilledAt,
          includedByTier: r.includedByTier ?? [],
        });
      }
    },
    onError: (e) => {
      const drifted =
        e instanceof ApiError && (e.body as { error?: string } | undefined)?.error === "plan_drifted";
      if (drifted) {
        // Reality changed under the form — close it; the admin reopens from fresh truth.
        setChangeForm(null);
        setChangePreview(null);
      }
      setError(billingError(e));
    },
  });
  const clearScheduledM = useMutation({
    mutationFn: (expectedAction: "cancel" | "pause") =>
      api(`/admin/billing/companies/${companyId}/clear-scheduled-change`, {
        method: "POST",
        body: JSON.stringify({ expectedAction }),
      }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(billingError(e)),
  });
  // The "applied — updates within a minute" banner clears itself once the webhook
  // has landed (the mirror's updatedAt moves — the query polls while the banner is
  // up, see refetchInterval above). Failsafe: stop the banner + polling after 5
  // minutes even if the webhook never arrives (the health panel owns that alarm).
  const subUpdatedAt = sub?.updatedAt;
  const appliedAtRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (changeApplied && appliedAtRef.current === undefined) {
      appliedAtRef.current = subUpdatedAt;
    } else if (changeApplied && appliedAtRef.current !== subUpdatedAt) {
      setChangeApplied(false);
      appliedAtRef.current = undefined;
    } else if (!changeApplied) {
      appliedAtRef.current = undefined;
    }
  }, [changeApplied, subUpdatedAt]);
  useEffect(() => {
    if (!changeApplied) return;
    const t = setTimeout(() => setChangeApplied(false), 5 * 60_000);
    return () => clearTimeout(t);
  }, [changeApplied]);

  const entMap = new Map((q.data?.entitlements ?? []).map((e) => [e.featureKey, e.source]));
  const currencyEnts = (q.data?.entitlements ?? [])
    .filter((e) => e.featureKey.startsWith("currency:"))
    .map((e) => e.featureKey.slice("currency:".length));

  const onSave = () => {
    if (!form) return;
    // graceUntil is dunning-only: carry the date for past_due, clear it for any other
    // status (a stale grace must not linger on an active sub), and when the admin
    // didn't change the day, resend the EXACT stored timestamp so a save doesn't pull
    // the deadline back to midnight.
    let graceUntil: string | null;
    if (form.status === "past_due") {
      const unchanged = sub?.graceUntil != null && form.graceUntil === sub.graceUntil.slice(0, 10);
      graceUntil = !form.graceUntil
        ? null
        : unchanged
          ? sub!.graceUntil
          : new Date(`${form.graceUntil}T00:00:00Z`).toISOString();
    } else {
      graceUntil = null;
    }
    saveM.mutate({
      status: form.status,
      tier: form.tier,
      traderSeatsExtra: form.traderSeatsExtra,
      currency: form.currency,
      graceUntil,
    });
  };

  return (
    <section className="space-y-4">
      <SectionTitle icon={<CreditCard size={18} />} title="EEX billing" />
      {q.isLoading ? (
        <div className="text-sm text-muted">Loading…</div>
      ) : q.isError ? (
        <div className="text-sm text-negRed">
          Couldn't load billing{q.error instanceof Error ? `: ${q.error.message}` : ""} — retry.
        </div>
      ) : !sub || !form ? (
        <div className="ops-admin-billing-content space-y-3 max-w-3xl">
          <OnboardingFeeCard onboarding={q.data?.onboarding} showTechnicalDetails />
          <div className="ops-panel rounded border border-border/60 p-3">
            <div className="text-xs font-medium text-fg">No current subscription</div>
            <p className="mt-1 text-[11px] leading-5 text-muted">
              Create the customer, invoice party, package, and first Paddle transaction in the
              reviewed onboarding workspace. Billing never creates an invoice or entitlement as
              a side effect of enabling a module.
            </p>
            <button
              type="button"
              onClick={() => openOnboardingM.mutate()}
              disabled={openOnboardingM.isPending || onboardingStatus === "paid" || onboardingStatus === "exempt"}
              className="mt-2 rounded border border-accent/50 px-3 py-1 text-xs text-accent hover:bg-accent/10 disabled:opacity-50"
            >
              {openOnboardingM.isPending ? "Opening…" : "Open reviewed onboarding workspace →"}
            </button>
            {onboardingStatus === "paid" || onboardingStatus === "exempt" ? (
              <p className="mt-2 text-[11px] text-muted">
                Onboarding is already satisfied. A future subscription-replacement workflow is
                required; the retired direct-invoice form cannot charge the onboarding fee again.
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="ops-admin-billing-content space-y-4 max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn("rounded border px-2 py-0.5 text-xs", billingStatusClass(sub.status))}>
              {billingStatusLabel(sub.status)}
            </span>
            <span className="text-xs text-muted">
              tier <span className="font-medium text-fg">{sub.tier}</span> · {sub.currency} · price book
              v{sub.priceBookVersion}
            </span>
            {sub.paddleSubscriptionId ? (
              <span className="rounded border border-amber/40 text-amber px-2 py-0.5 text-xs">
                Paddle-managed (read-only)
              </span>
            ) : null}
          </div>

          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <MiniStat label="Collection" value={sub.collectionMode ? billingStatusLabel(sub.collectionMode) : "—"} />
            <MiniStat label="Current period" value={sub.currentPeriodStart && sub.currentPeriodEnd ? `${sub.currentPeriodStart.slice(0, 10)} → ${sub.currentPeriodEnd.slice(0, 10)}` : "—"} />
            <MiniStat label="Next billed" value={sub.nextBilledAt?.slice(0, 10) ?? "—"} />
            <MiniStat label="Scheduled change" value={sub.scheduledPauseAt ? `Pause ${sub.scheduledPauseAt.slice(0, 10)}` : sub.scheduledCancelAt ? `Cancel ${sub.scheduledCancelAt.slice(0, 10)}` : "None"} />
          </div>

          {sub.scheduledCancelAt && q.data?.finalTrueUpGuard ? (
            <div className={cn(
              "rounded border px-3 py-2.5 text-xs",
              q.data.finalTrueUpGuard.blocking
                ? "border-negRed/40 bg-negRed/5"
                : q.data.finalTrueUpGuard.state === "billed"
                  ? "border-posGreen/40 bg-posGreen/5"
                  : "border-amber/40 bg-amber/5"
            )}>
              <div className="font-medium text-fg">
                {q.data.finalTrueUpGuard.blocking
                  ? "Final true-up blocks safe offboarding"
                  : q.data.finalTrueUpGuard.state === "billed"
                    ? "Final true-up billed"
                    : q.data.finalTrueUpGuard.state === "waived"
                      ? "Final true-up explicitly waived"
                      : "Final true-up still required"}
              </div>
              <div className="mt-1 leading-5 text-muted">
                {finalTrueUpMessage(q.data.finalTrueUpGuard)}
              </div>
            </div>
          ) : null}

          <OnboardingFeeCard onboarding={q.data?.onboarding} showTechnicalDetails />

          {/* Access control — suspension is propose-and-confirm (works on Paddle subs too) */}
          <div className="flex flex-wrap items-center gap-2">
            {sub.status === "suspended" ? (
              <button
                type="button"
                onClick={() => resumeM.mutate()}
                disabled={resumeM.isPending}
                className="rounded border border-posGreen/50 text-posGreen px-3 py-1 text-xs hover:bg-posGreen/10 disabled:opacity-50"
              >
                Resume access
              </button>
            ) : (
              <>
                {suspensionProposed ? (
                  <span className="rounded border border-amber/40 text-amber px-2 py-0.5 text-xs">
                    Grace elapsed — suspension proposed
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => suspendM.mutate()}
                  disabled={suspendM.isPending}
                  title="Cuts this company's EEX access immediately. Reversible via Resume."
                  className={cn(
                    "rounded border px-3 py-1 text-xs disabled:opacity-50",
                    suspensionProposed
                      ? "border-negRed/60 text-negRed hover:bg-negRed/10"
                      : "border-border text-muted hover:bg-muted/10"
                  )}
                >
                  Suspend access
                </button>
              </>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 max-w-lg">
            <MiniStat label="Trader seats used" value={String(q.data?.seatUsage.used ?? 0)} />
            <MiniStat
              label="Seats allowed"
              value={q.data?.seatUsage.allowed === null ? "∞ (unlimited)" : String(q.data?.seatUsage.allowed)}
            />
            <MiniStat label="GWh / mo included" value={sub.gwhIncluded ?? "∞"} />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-xs text-muted">
              Status
              <select
                value={form.status}
                disabled={Boolean(sub.paddleSubscriptionId)}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="mt-1 w-full bg-transparent border border-border rounded px-2 py-1.5 text-sm"
              >
                {BILLING_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {billingStatusLabel(s)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-muted">
              Tier
              <select
                value={form.tier}
                disabled={Boolean(sub.paddleSubscriptionId)}
                onChange={(e) => setForm({ ...form, tier: e.target.value })}
                className="mt-1 w-full bg-transparent border border-border rounded px-2 py-1.5 text-sm"
              >
                {BILLING_TIER_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-muted">
              Extra trader seats
              <input
                type="number"
                min={0}
                value={form.traderSeatsExtra}
                disabled={Boolean(sub.paddleSubscriptionId)}
                onChange={(e) => setForm({ ...form, traderSeatsExtra: Math.max(0, Number(e.target.value) || 0) })}
                className="mt-1 w-full bg-transparent border border-border rounded px-2 py-1.5 text-sm"
              />
            </label>
            <label className="text-xs text-muted">
              Currency
              <select
                value={form.currency}
                disabled={Boolean(sub.paddleSubscriptionId)}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="mt-1 w-full bg-transparent border border-border rounded px-2 py-1.5 text-sm"
              >
                {["GBP", "EUR"].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            {form.status === "past_due" ? (
              <label className="text-xs text-muted">
                Grace until (empty ⇒ +{2} days)
                <input
                  type="date"
                  value={form.graceUntil}
                  onChange={(e) => setForm({ ...form, graceUntil: e.target.value })}
                  className="mt-1 w-full bg-transparent border border-border rounded px-2 py-1.5 text-sm"
                />
              </label>
            ) : null}
          </div>

          <button
            type="button"
            onClick={onSave}
            disabled={saveM.isPending || Boolean(sub.paddleSubscriptionId)}
            className="inline-flex items-center gap-1 rounded bg-accent text-white px-3 py-1.5 text-sm disabled:opacity-50"
          >
            <Save size={14} />
            Save subscription
          </button>

          {/* All initial Paddle issuance is owned by the route-addressable onboarding
              operation. Keeping a second invoice form here previously allowed party,
              package, review, and no-payment-link controls to be bypassed. */}
          {!sub.paddleSubscriptionId ? (
            <div className="ops-panel border border-border/60 rounded p-3 space-y-2 max-w-md">
              <div className="text-xs font-medium text-fg">Paddle subscription setup</div>
              {onboardingStatus === null || onboardingStatus === "required" ? (
                <p className="text-[11px] text-muted">
                  Start the reviewed onboarding operation. Its first invoice includes the mandatory {formatBillingMoney(
                    ONBOARDING_FEE_MINOR,
                    ONBOARDING_FEE_CURRENCY
                  )} one-time onboarding fee, excluding tax, exactly once with the recurring plan.
                  The default is a Net-14 bank-transfer invoice with no payment link;
                  there is no omit or waive option.
                </p>
              ) : onboardingStatus === "paid" || onboardingStatus === "exempt" ? (
                <p className="text-[11px] text-muted">
                  The onboarding requirement is already satisfied. Direct replacement issuance is
                  intentionally unavailable here, so the onboarding fee cannot be charged twice.
                  Use a dedicated, reviewed subscription-replacement operation when that workflow
                  is implemented.
                </p>
              ) : onboardingStatus === "invoiced" ? (
                <p className="text-[11px] text-muted">
                  The onboarding invoice has already been issued. A duplicate invoice is blocked;
                  wait for Paddle payment or webhook reconciliation.
                </p>
              ) : onboardingStatus === "canceled" || onboardingStatus === "reversed" ? (
                <p className="text-[11px] text-negRed">
                  The onboarding purchase is {onboardingStatus}. A separate audited reissue is
                  required before another invoice can be issued.
                </p>
              ) : (
                <p className="text-[11px] text-muted">
                  The onboarding state is not available yet. Invoice issuance is blocked until it
                  can be verified.
                </p>
              )}
              {onboardingStatus === null || onboardingStatus === "required" ? (
                <button
                  type="button"
                  onClick={() => openOnboardingM.mutate()}
                  disabled={openOnboardingM.isPending}
                  className="rounded border border-accent/50 text-accent px-3 py-1 text-xs hover:bg-accent/10 disabled:opacity-50"
                >
                  {openOnboardingM.isPending ? "Opening…" : "Open reviewed onboarding workspace →"}
                </button>
              ) : null}
            </div>
          ) : null}

          <div>
            <div className="text-xs text-muted mb-1">
              Licensed features (tier-included are locked; click an add-on to grant/revoke)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {EEX_FEATURES.map((f) => {
                const source = entMap.get(f.key);
                const isTier = source === "tier";
                const isOn = source !== undefined;
                return (
                  <button
                    key={f.key}
                    type="button"
                    disabled={isTier || featureM.isPending}
                    onClick={() => featureM.mutate({ key: f.key, active: !isOn, source: "addon" })}
                    title={
                      isTier
                        ? "Included in tier"
                        : isOn
                          ? `Licensed (${source}) — click to revoke`
                          : "Click to grant as add-on"
                    }
                    className={cn(
                      "rounded border px-2 py-0.5 text-xs",
                      isTier
                        ? "border-posGreen/40 text-posGreen opacity-70 cursor-default"
                        : isOn
                          ? "border-accent/50 text-accent hover:bg-accent/10"
                          : "border-border text-muted hover:bg-muted/10"
                    )}
                  >
                    {f.label}
                    {isOn ? ` · ${source}` : ""}
                  </button>
                );
              })}
            </div>
            {currencyEnts.length > 0 ? (
              <div className="mt-2 text-[11px] text-muted">
                Non-EUR currencies licensed: {currencyEnts.join(", ")}
              </div>
            ) : null}
          </div>

          {/* WP7: mid-term plan change for Paddle-managed subs (preview → apply).
              Seeded from the server's live-item view; apply is drift-guarded. */}
          {sub.paddleSubscriptionId && sub.status !== "canceled" ? (
            <div className="ops-panel border border-border/60 rounded p-3 space-y-3 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-xs font-medium text-fg">Paddle plan</div>
                {sub.scheduledCancelAt ? (
                  <>
                    <span className="rounded border border-amber/40 text-amber px-2 py-0.5 text-xs">
                      Cancellation scheduled for {sub.scheduledCancelAt.slice(0, 10)}
                    </span>
                    <button
                      type="button"
                      onClick={() => clearScheduledM.mutate("cancel")}
                      disabled={clearScheduledM.isPending}
                      className="rounded border border-border text-muted px-2 py-0.5 text-xs hover:bg-muted/10 disabled:opacity-50"
                    >
                      Remove scheduled cancellation
                    </button>
                  </>
                ) : null}
                {sub.scheduledPauseAt ? (
                  <>
                    <span className="rounded border border-amber/40 text-amber px-2 py-0.5 text-xs">
                      Pause scheduled for {sub.scheduledPauseAt.slice(0, 10)}
                    </span>
                    <button
                      type="button"
                      onClick={() => clearScheduledM.mutate("pause")}
                      disabled={clearScheduledM.isPending}
                      className="rounded border border-border text-muted px-2 py-0.5 text-xs hover:bg-muted/10 disabled:opacity-50"
                    >
                      Remove scheduled pause
                    </button>
                  </>
                ) : null}
                {!changeForm ? (
                  <button
                    type="button"
                    onClick={() => openChangeM.mutate(companyId)}
                    disabled={openChangeM.isPending}
                    className="rounded border border-accent/50 text-accent px-3 py-1 text-xs hover:bg-accent/10 disabled:opacity-50"
                  >
                    {openChangeM.isPending ? "Loading current plan…" : "Change plan…"}
                  </button>
                ) : null}
                {changeApplied ? (
                  <span className="text-[11px] text-posGreen">
                    Change applied — Paddle confirms via webhook, this page updates within a minute.
                  </span>
                ) : null}
              </div>

              {changeForm ? (
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-3 gap-3">
                    <label className="text-xs text-muted">
                      Tier
                      <select
                        value={changeForm.desired.tier}
                        disabled={changeM.isPending}
                        onChange={(e) =>
                          editChange({ desired: { ...changeForm.desired, tier: e.target.value } })
                        }
                        className="mt-1 w-full bg-transparent border border-border rounded px-2 py-1.5 text-sm"
                      >
                        {BILLING_TIER_OPTIONS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-muted">
                      Extra trader seats
                      <input
                        type="number"
                        min={0}
                        max={1000}
                        step={1}
                        value={changeForm.desired.extraSeats}
                        disabled={changeM.isPending}
                        onChange={(e) => {
                          const n = Math.min(1000, Math.max(0, Math.floor(Number(e.target.value) || 0)));
                          editChange({ desired: { ...changeForm.desired, extraSeats: n } });
                        }}
                        className="mt-1 w-full bg-transparent border border-border rounded px-2 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs text-muted">
                      Billing of the difference
                      <select
                        value={changeForm.prorationMode}
                        disabled={changeM.isPending}
                        onChange={(e) => editChange({ prorationMode: e.target.value })}
                        className="mt-1 w-full bg-transparent border border-border rounded px-2 py-1.5 text-sm"
                      >
                        {/* Manual (invoice) collection — Paddle rejects bill-now modes. */}
                        <option value="prorated_next_billing_period">Prorated, on the next invoice</option>
                        <option value="full_next_billing_period">Full price, on the next invoice</option>
                        <option value="do_not_bill">Don't bill the difference</option>
                      </select>
                    </label>
                  </div>

                  <div>
                    <div className="text-[11px] text-muted mb-1">Paid add-ons (billed via Paddle)</div>
                    <div className="flex flex-wrap gap-1.5">
                      {EEX_FEATURES.map((f) => {
                        const on = changeForm.desired.addons.includes(f.key);
                        return (
                          <button
                            key={f.key}
                            type="button"
                            disabled={changeM.isPending}
                            onClick={() =>
                              editChange({
                                desired: {
                                  ...changeForm.desired,
                                  addons: on
                                    ? changeForm.desired.addons.filter((k) => k !== f.key)
                                    : [...changeForm.desired.addons, f.key],
                                },
                              })
                            }
                            className={cn(
                              "rounded border px-2 py-0.5 text-xs disabled:opacity-50",
                              on
                                ? "border-accent/50 text-accent hover:bg-accent/10"
                                : "border-border text-muted hover:bg-muted/10"
                            )}
                          >
                            {f.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-[11px] text-muted mb-1">
                      Extra billing currencies (paid add-on on Bronze–Gold; included on Platinum)
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {/* Keep in sync with SUPPORTED_EXTRA_CURRENCIES (paddle-catalog.ts). */}
                      {["GBP"].map((iso) => {
                        const on = changeForm.desired.currencies.includes(iso);
                        return (
                          <button
                            key={iso}
                            type="button"
                            disabled={changeM.isPending}
                            onClick={() =>
                              editChange({
                                desired: {
                                  ...changeForm.desired,
                                  currencies: on
                                    ? changeForm.desired.currencies.filter((c) => c !== iso)
                                    : [...changeForm.desired.currencies, iso],
                                },
                              })
                            }
                            className={cn(
                              "rounded border px-2 py-0.5 text-xs disabled:opacity-50",
                              on
                                ? "border-accent/50 text-accent hover:bg-accent/10"
                                : "border-border text-muted hover:bg-muted/10"
                            )}
                          >
                            {iso}
                          </button>
                        );
                      })}
                    </div>
                    {changeForm.expectedCurrent.tier === "platinum" &&
                    changeForm.desired.tier !== "platinum" &&
                    changeForm.desired.currencies.length === 0 ? (
                      <p className="mt-1 text-[11px] text-amber">
                        Leaving Platinum: extra currencies were included — if this company bills in a
                        non-EUR currency, add it above or the licence closes with the downgrade.
                      </p>
                    ) : null}
                    <p className="mt-1 text-[11px] text-muted">
                      Add-ons the target tier already includes are folded in automatically (shown in
                      the preview); the change is refused if the plan changed elsewhere in the meantime.
                    </p>
                  </div>

                  {changePreview && changePreview.version === changeForm.version ? (
                    <div className="text-xs border border-border/60 rounded px-2 py-1.5">
                      Preview: charge{" "}
                      <span className="font-mono">
                        {changePreview.charge
                          ? `${(Number(changePreview.charge.amount) / 100).toLocaleString()} ${changePreview.charge.currency}`
                          : "0"}
                      </span>
                      {" · "}credit{" "}
                      <span className="font-mono">
                        {changePreview.credit
                          ? `${(Number(changePreview.credit.amount) / 100).toLocaleString()} ${changePreview.credit.currency}`
                          : "0"}
                      </span>
                      {changePreview.nextBilledAt ? (
                        <>
                          {" · "}next invoice {changePreview.nextBilledAt.slice(0, 10)}
                        </>
                      ) : null}
                      {changePreview.includedByTier.length > 0 ? (
                        <>
                          {" · "}included by tier: {changePreview.includedByTier.join(", ")}
                        </>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => changeM.mutate({ apply: false, form: changeForm, forCompanyId: companyId })}
                      disabled={changeM.isPending}
                      className="rounded border border-accent/50 text-accent px-3 py-1 text-xs hover:bg-accent/10 disabled:opacity-50"
                    >
                      Preview cost
                    </button>
                    <button
                      type="button"
                      onClick={() => changeM.mutate({ apply: true, form: changeForm, forCompanyId: companyId })}
                      disabled={
                        changeM.isPending ||
                        !changePreview ||
                        changePreview.version !== changeForm.version
                      }
                      title={
                        changePreview && changePreview.version === changeForm.version
                          ? "Apply this change in Paddle"
                          : "Preview first"
                      }
                      className="rounded bg-accent text-white px-3 py-1 text-xs disabled:opacity-50"
                    >
                      Apply change
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setChangeForm(null);
                        setChangePreview(null);
                      }}
                      className="rounded border border-border text-muted px-3 py-1 text-xs hover:bg-muted/10"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <BillingUsageBlock key={companyId} companyId={companyId} onError={setError} />

          {sub.status === "comped" ? (
            <div className="text-[11px] text-muted">
              Complimentary / all-inclusive — set a real tier + status to convert to a paid plan.
            </div>
          ) : null}
          {error ? <div className="text-xs text-negRed">{error}</div> : null}
        </div>
      )}
    </section>
  );
}

type BillingUsageSnapshot = {
  id: string;
  periodStart: string;
  kind: string;
  mwhTraded: string;
  mwhIncluded: string;
  overageGwh: string;
  overageAmountMinor: number;
  currency: string;
  status: string;
  isComplete?: boolean;
  invoice?: {
    state: string;
    lastErrorCode: string | null;
    paddleTransactionId: string | null;
    invoiceNumber: string | null;
    status: string | null;
    totalMinor: number | null;
    taxMinor: number | null;
    dueAt: string | null;
    billedAt: string | null;
    paidAt: string | null;
  } | null;
};

// ── Company-admin READ-ONLY billing card ────────────────────────────────────
// The customer view of their own EEX subscription: tier, licensed features,
// seat usage, metered GWh history. Management lives in the global-admin
// BillingSection; nothing here mutates.

type CompanyBillingSummary = {
  subscription: {
    status: string;
    tier: string;
    currency: string;
    traderSeatsIncluded: number | null;
    traderSeatsExtra: number;
    gwhIncluded: string | null;
    currentPeriodEnd: string | null;
    graceUntil: string | null;
  } | null;
  onboarding?: CompanyOnboardingSummary | null;
  features: string[];
  seatUsage: { used: number; allowed: number | null } | null;
  usage: Omit<BillingUsageSnapshot, "id" | "status">[];
};

export function CompanyBillingCard() {
  // Key by the authed user so cached billing data can never render for a different
  // user on the same SPA session (the cache is also cleared on logout).
  const userId = useAuth((s) => s.user?.userId);
  const q = useQuery({
    queryKey: ["billing", "summary", userId],
    queryFn: () => api<CompanyBillingSummary>("/billing/summary"),
    retry: false,
  });
  const sub = q.data?.subscription;

  // On the dedicated Billing tab the heading always renders — a bare null here
  // (fine when this was one card on a stacked page) would read as a blank tab.
  if (!sub) {
    return (
      <section className="space-y-4">
        <SectionTitle icon={<CreditCard size={18} />} title="EEX subscription" />
        {q.data ? <OnboardingFeeCard onboarding={q.data.onboarding} /> : null}
        <div className="text-sm text-muted">
          {q.isPending
            ? "Loading…"
            : q.isError
              ? "Couldn't load the billing summary."
              : "No EEX subscription for this company."}
        </div>
      </section>
    );
  }

  const featureLabel = (key: string) =>
    EEX_FEATURES.find((f) => f.key === key)?.label ??
    (key.startsWith("currency:") ? `Currency: ${key.slice("currency:".length)}` : key);
  const fmtGwh = (mwh: string) => (Number(mwh) / 1000).toFixed(1);
  const unlimited = sub.gwhIncluded === null;

  return (
    <section className="space-y-4">
      <SectionTitle icon={<CreditCard size={18} />} title="EEX subscription" />
      <div className="ops-admin-billing-content space-y-4 max-w-3xl">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("rounded border px-2 py-0.5 text-xs", billingStatusClass(sub.status))}>
            {billingStatusLabel(sub.status)}
          </span>
          <span className="text-xs text-muted">
            tier <span className="font-medium text-fg">{sub.tier}</span> · {sub.currency}
            {sub.currentPeriodEnd ? ` · renews ${sub.currentPeriodEnd.slice(0, 10)}` : ""}
            {sub.status === "past_due" && sub.graceUntil
              ? ` · access until ${sub.graceUntil.slice(0, 10)}`
              : ""}
          </span>
        </div>

        <OnboardingFeeCard onboarding={q.data?.onboarding} />

        <div className="grid grid-cols-3 gap-2 max-w-lg">
          <MiniStat label="Trader seats used" value={String(q.data?.seatUsage?.used ?? 0)} />
          <MiniStat
            label="Seats included"
            value={
              q.data?.seatUsage?.allowed === null ? "∞" : String(q.data?.seatUsage?.allowed ?? 0)
            }
          />
          <MiniStat label="GWh / mo included" value={unlimited ? "∞" : Number(sub.gwhIncluded).toFixed(0)} />
        </div>

        {(q.data?.features.length ?? 0) > 0 ? (
          <div>
            <div className="text-xs text-muted mb-1">Licensed features</div>
            <div className="flex flex-wrap gap-1.5">
              {q.data!.features.map((k) => (
                <span key={k} className="rounded border border-posGreen/40 text-posGreen px-2 py-0.5 text-xs">
                  {featureLabel(k)}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {(q.data?.usage.length ?? 0) > 0 ? (
          <div>
            <div className="text-xs text-muted mb-1">
              Traded volume by month{unlimited ? " (all-inclusive plan — shown for reference)" : ""}
            </div>
            <div className="ops-table-frame border border-border rounded-md overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] text-muted border-b border-border">
                  <tr>
                    <th className="text-left p-1.5">Month</th>
                    <th className="text-right p-1.5">Traded GWh</th>
                    <th className="text-right p-1.5">Included GWh</th>
                    <th className="text-right p-1.5">Overage GWh</th>
                    <th className="text-right p-1.5">Overage</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {q.data!.usage.map((s) => (
                    <tr key={s.periodStart} className="border-b border-border/60 last:border-0">
                      <td className="p-1.5">
                        {s.periodStart.slice(0, 7)}
                        {s.isComplete === false ? " (to date)" : ""}
                        {s.kind !== "monthly" ? ` (${s.kind})` : ""}
                      </td>
                      <td className="p-1.5 text-right">{fmtGwh(s.mwhTraded)}</td>
                      <td className="p-1.5 text-right">{unlimited ? "∞" : fmtGwh(s.mwhIncluded)}</td>
                      <td className="p-1.5 text-right">{Number(s.overageGwh).toFixed(1)}</td>
                      <td className="p-1.5 text-right">
                        {(s.overageAmountMinor / 100).toFixed(2)} {s.currency}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function usageStatusClass(s: string): string {
  if (s === "approved" || s === "billed") return "border-posGreen/40 text-posGreen";
  if (s === "waived") return "border-border text-muted";
  if (s === "disputed") return "border-negRed/40 text-negRed";
  return "border-amber/40 text-amber"; // computed (awaiting review), adjusted
}

// Approval records review. Issuing a separate Paddle invoice is an explicit action.
// The last CLOSED month in EEX venue time (CET/CEST) — mirrors the backend's
// prevMonthLabelCet so the default (and the input max) can't drift to a month
// the server would 422 as not-closed. Plain UTC month math is off by one for
// the first hours of a CET month.
function prevMonthLabelCetUi(): string {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
  });
  const [y, m] = fmt.format(new Date()).split("-").map(Number);
  const prevY = m === 1 ? y! - 1 : y!;
  const prevM = m === 1 ? 12 : m! - 1;
  return `${prevY}-${String(prevM).padStart(2, "0")}`;
}

function BillingUsageBlock({
  companyId,
  onError,
}: {
  companyId: string;
  onError: (msg: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const lastClosedMonth = prevMonthLabelCetUi();
  const [month, setMonth] = useState(lastClosedMonth);
  const [reviewInvoiceId, setReviewInvoiceId] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["admin", "billing", companyId, "usage"],
    queryFn: () =>
      api<{ snapshots: BillingUsageSnapshot[] }>(`/admin/billing/companies/${companyId}/usage`),
    retry: false,
    refetchInterval: query => query.state.data?.snapshots.some(s => s.invoice &&
      (["creating", "issuing"].includes(s.invoice.state) || (s.invoice.state === "issued" && !s.invoice.invoiceNumber))) ? 10_000 : false,
  });
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["admin", "billing", companyId, "usage"] });

  const recomputeM = useMutation({
    mutationFn: () =>
      api(`/admin/billing/companies/${companyId}/usage/recompute`, {
        method: "POST",
        body: JSON.stringify({ month }),
      }),
    onSuccess: () => {
      onError(null);
      invalidate();
    },
    onError: (e) => onError(billingError(e)),
  });
  const statusM = useMutation({
    mutationFn: (v: { id: string; verb: "approve" | "waive" }) =>
      api(`/admin/billing/usage/${v.id}/${v.verb}`, { method: "POST", body: JSON.stringify({}) }),
    onSuccess: () => {
      onError(null);
      invalidate();
    },
    onError: (e) => onError(billingError(e)),
  });

  const invoiceM = useMutation({
    mutationFn: (v: { id: string; action: "invoice" | "refresh-invoice" }) =>
      api(`/admin/billing/usage/${v.id}/${v.action}`, { method: "POST",
        body: JSON.stringify(v.action === "invoice" ? { acknowledgement: "issue_overage_invoice" } : {}) }),
    onSuccess: () => { onError(null); setReviewInvoiceId(null); },
    onError: (error) => { onError(billingError(error)); setReviewInvoiceId(null); },
    onSettled: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ["admin", "transactions"] });
    },
  });

  const rows = q.data?.snapshots ?? [];
  const fmtGwh = (mwh: string) => (Number(mwh) / 1000).toFixed(1);
  const fmtMoney = (minor: number, ccy: string) =>
    `${(minor / 100).toFixed(2)} ${ccy}`;
  const reviewing = rows.find(s => s.id === reviewInvoiceId);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="text-xs text-muted">Metered usage — approve, then send each overage invoice to Paddle</div>
        <div className="flex items-center gap-1.5">
          <input
            type="month"
            value={month}
            max={lastClosedMonth}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-transparent border border-border rounded px-2 py-1 text-xs"
          />
          <button
            type="button"
            onClick={() => recomputeM.mutate()}
            disabled={recomputeM.isPending || !/^\d{4}-\d{2}$/.test(month)}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted/10 disabled:opacity-50"
          >
            Compute month
          </button>
        </div>
      </div>
      {reviewing && (
        <div role="dialog" aria-label="Review overage invoice" className="my-3 rounded border border-accent/40 p-3 text-sm space-y-2">
          <div className="font-medium">Send overage invoice for {reviewing.periodStart.slice(0, 7)}</div>
          <p>{fmtMoney(reviewing.overageAmountMinor, reviewing.currency)} excluding applicable tax, for {reviewing.overageGwh} GWh above the allowance.</p>
          <p className="text-xs text-muted">Paddle will issue and email a separate invoice to this company’s subscription billing recipient, using its existing payment terms. This does not change the recurring plan. Repeated submissions reuse the same invoice.</p>
          <div className="flex gap-2">
            <button type="button" disabled={invoiceM.isPending} className="rounded bg-accent text-white px-3 py-1.5 disabled:opacity-50"
              onClick={() => invoiceM.mutate({ id: reviewing.id, action: "invoice" })}>{invoiceM.isPending ? "Sending…" : "Confirm and send invoice"}</button>
            <button type="button" disabled={invoiceM.isPending} className="rounded border border-border px-3 py-1.5" onClick={() => setReviewInvoiceId(null)}>Cancel</button>
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-xs text-muted">
          No usage snapshots yet — the cron computes the previous month on the 1st, or compute one
          above.
        </div>
      ) : (
        <div className="ops-table-frame border border-border rounded-md overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] text-muted border-b border-border">
              <tr>
                <th className="text-left p-1.5">Month</th>
                <th className="text-right p-1.5">Traded GWh</th>
                <th className="text-right p-1.5">Included GWh</th>
                <th className="text-right p-1.5">Overage GWh</th>
                <th className="text-right p-1.5">Overage</th>
                <th className="text-left p-1.5">Status</th>
                <th className="text-left p-1.5">Paddle invoice</th>
                <th className="text-right p-1.5">Actions</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {rows.map((s) => (
                <tr key={s.id} className="border-b border-border/60 last:border-0">
                  <td className="p-1.5">
                    {s.periodStart.slice(0, 7)}
                    {s.kind !== "monthly" ? ` (${s.kind})` : ""}
                  </td>
                  <td className="p-1.5 text-right">{fmtGwh(s.mwhTraded)}</td>
                  <td className="p-1.5 text-right">{fmtGwh(s.mwhIncluded)}</td>
                  <td className="p-1.5 text-right">{Number(s.overageGwh).toFixed(1)}</td>
                  <td className="p-1.5 text-right">{fmtMoney(s.overageAmountMinor, s.currency)}</td>
                  <td className="p-1.5 font-sans">
                    <span className={cn("rounded border px-1.5 py-0.5", usageStatusClass(s.status))}>
                      {s.status}
                    </span>
                  </td>
                  <td className="p-1.5 font-sans text-xs">
                    {s.invoice ? <div className="space-y-1">
                      <div>{s.invoice.invoiceNumber ?? (s.invoice.paddleTransactionId ? "Awaiting invoice number" : "Submission recorded")}</div>
                      <div className="text-muted">{s.invoice.status ?? ({ creating: "Submitting", issuing: "Issuing", ready: "Ready to issue", rejected: "Rejected — not sent", review_required: "Needs reconciliation", issued: "Issued" }[s.invoice.state] ?? s.invoice.state)}</div>
                      {s.invoice.totalMinor != null && <div>Total: {fmtMoney(s.invoice.totalMinor, s.currency)}{s.invoice.taxMinor != null ? ` (tax ${fmtMoney(s.invoice.taxMinor, s.currency)})` : ""}</div>}
                      {s.invoice.dueAt && <div>Due {s.invoice.dueAt.slice(0, 10)}</div>}
                      {s.invoice.paidAt && <div className="text-posGreen">Paid {s.invoice.paidAt.slice(0, 10)}</div>}
                      {s.invoice.paddleTransactionId && <div className="font-mono text-[10px] break-all">{s.invoice.paddleTransactionId}</div>}
                      {s.invoice.invoiceNumber && s.invoice.paddleTransactionId && <a className="text-accent underline" target="_blank" rel="noreferrer" href={`/api/admin/transactions/${encodeURIComponent(s.invoice.paddleTransactionId)}/invoice-pdf`}>Invoice PDF</a>}
                      {s.invoice.state === "review_required" && <p className="max-w-xs text-amber">Refresh to check the existing submission. Creating another invoice is blocked.</p>}
                    </div> : <span className="text-muted">Not sent</span>}
                  </td>
                  <td className="p-1.5 text-right font-sans">
                    {s.status === "approved" && s.overageAmountMinor > 0 && (!s.invoice || ["ready", "rejected"].includes(s.invoice.state)) && (
                      <button type="button" disabled={invoiceM.isPending || statusM.isPending} className="rounded border border-accent text-accent px-2 py-1 mr-1 disabled:opacity-50"
                        onClick={() => { onError(null); setReviewInvoiceId(s.id); }}>{s.invoice?.state === "ready" ? "Issue existing invoice" : "Send to Paddle"}</button>
                    )}
                    {s.invoice && <button type="button" disabled={invoiceM.isPending} className="rounded border border-border px-2 py-1 mr-1 disabled:opacity-50"
                      onClick={() => invoiceM.mutate({ id: s.id, action: "refresh-invoice" })}>Refresh invoice status</button>}
                    {s.status === "computed" && !s.invoice ? (
                      <button
                        type="button"
                        onClick={() => statusM.mutate({ id: s.id, verb: "approve" })}
                        disabled={statusM.isPending}
                        className="rounded border border-border px-1.5 py-0.5 hover:bg-muted/10 mr-1"
                      >
                        Approve
                      </button>
                    ) : null}
                    {(s.status === "computed" || s.status === "approved") && !s.invoice ? (
                      <button
                        type="button"
                        onClick={() => statusM.mutate({ id: s.id, verb: "waive" })}
                        disabled={statusM.isPending}
                        className="rounded border border-border px-1.5 py-0.5 hover:bg-muted/10"
                      >
                        Waive
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
